import type * as Api from '@ViewModels';
import { useCallback, useState } from 'react';

import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useUserPreferencesMutation } from '../../../queries/User/useUserPreferencesMutation';
import { COACHING_TEMPLATE_LS_KEY, type UseCoachingPreferencesResult } from './types';

/**
 * Backs the focused-view Settings drawer panel.
 *
 * Realtime + auto-suggestion toggles persist via `useUserPreferencesMutation`
 * (server-side `coffeePreferences`); the AI-agent template ID persists in
 * localStorage under `focused-view:coaching-template-id`.
 *
 * TODO follow-up: wire `selectedCoachingTemplateId` into the focused-view
 * coaching pipeline (`focused-view/ai/useCoaching`) so picking a template here
 * actually changes the system prompt for the next conversation. Today the
 * dropdown is a UX placeholder that stores the selection but does not apply
 * it. The legacy queue applies it via `useTysonCoaching`'s MobX context,
 * which the focused-view module deliberately does not import.
 */
export function useCoachingPreferences(): UseCoachingPreferencesResult {
	const userSession = useUserSession();
	const realtimeCoachingEnabled =
		userSession.user?.userPreferences?.coffeePreferences?.realtimeCoachingEnabled ?? false;
	const autoSuggestionsEnabled = userSession.user?.userPreferences?.coffeePreferences?.autoSuggestionsEnabled ?? false;

	const [selectedCoachingTemplateId, setTemplateState] = useState<string | null>(() => {
		try {
			return window.localStorage.getItem(COACHING_TEMPLATE_LS_KEY);
		} catch {
			return null;
		}
	});

	const mutation = useUserPreferencesMutation({
		onSuccess: (next: Api.IUserPreferences) => {
			// Mirror the legacy queue's pattern: stamp the new prefs back onto
			// the user session so the next render sees them.
			if (userSession.user) {
				userSession.user.userPreferences = next;
			}
		},
	});

	const setRealtimeCoaching = useCallback(
		async (enabled: boolean) => {
			await mutation.mutateAsync({
				preferences: {
					coffeePreferences: {
						...userSession.user?.userPreferences?.coffeePreferences,
						realtimeCoachingEnabled: enabled,
					},
				} as Api.IUserPreferences,
			});
		},
		[mutation, userSession]
	);

	const setAutoSuggestions = useCallback(
		async (enabled: boolean) => {
			await mutation.mutateAsync({
				preferences: {
					coffeePreferences: {
						...userSession.user?.userPreferences?.coffeePreferences,
						autoSuggestionsEnabled: enabled,
					},
				} as Api.IUserPreferences,
			});
		},
		[mutation, userSession]
	);

	const setCoachingTemplate = useCallback((id: string | null) => {
		setTemplateState(id);
		try {
			if (id == null) {
				window.localStorage.removeItem(COACHING_TEMPLATE_LS_KEY);
			} else {
				window.localStorage.setItem(COACHING_TEMPLATE_LS_KEY, id);
			}
		} catch {
			// ignore storage failures (private mode etc.) — UI state stays in memory
		}
	}, []);

	return {
		realtimeCoachingEnabled,
		autoSuggestionsEnabled,
		selectedCoachingTemplateId,
		setRealtimeCoaching,
		setAutoSuggestions,
		setCoachingTemplate,
		isMutating: mutation.isPending,
	};
}
